import { Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Gavel, Timer } from 'lucide-react'
import { api } from '@/lib/api'
import { localizedName } from '@/lib/format'
import { useAppStore } from '@/lib/store'
import { useT, getT } from '@/lib/i18n'
import type { AuctionSummary } from '@/shared/types'
import { StorefrontPage, StorefrontCard, StorefrontEmptyState, StorefrontLoading } from '@/components/StorefrontPage'

interface PublicSettings {
  auctions_enabled: boolean
}

function formatTimeLeft(seconds: number, lang: 'uk' | 'en' | 'ru') {
  if (seconds <= 0) return getT(lang, 'auctions').ended
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  if (lang === 'en') return h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${s}s` : `${s}s`
  if (lang === 'ru') return h > 0 ? `${h} ч ${m} мин` : m > 0 ? `${m} мин ${s} с` : `${s} с`
  return h > 0 ? `${h} год ${m} хв` : m > 0 ? `${m} хв ${s} с` : `${s} с`
}

export function AuctionsPage() {
  const lang = useAppStore((s) => s.lang)
  const T = useT('auctions')

  const { data: settings, isLoading: settingsLoading } = useQuery<PublicSettings>({
    queryKey: ['public-settings'],
    queryFn: () => api.get<PublicSettings>('/settings'),
    staleTime: 300_000,
  })
  const auctionsEnabled = Boolean(settings?.auctions_enabled)
  const { data, isLoading } = useQuery<AuctionSummary[]>({
    queryKey: ['auctions'],
    queryFn: () => api.get<AuctionSummary[]>('/auctions'),
    refetchInterval: 10_000,
    enabled: auctionsEnabled,
  })

  if (settingsLoading || (auctionsEnabled && isLoading)) {
    return <StorefrontLoading message={T.loading} />
  }
  if (!auctionsEnabled) {
    return (
      <StorefrontEmptyState
        icon={Gavel}
        title={T.disabled}
      />
    )
  }
  if (!data?.length) {
    return (
      <StorefrontEmptyState
        icon={Gavel}
        title={T.empty}
      />
    )
  }

  return (
    <StorefrontPage
      title={T.title}
      badge={data.length}
    >
      <ul className="space-y-3">
        {data.map((auction) => {
          const productName = localizedName(lang, auction.product_name_uk, auction.product_name_en)
          return (
            <li key={auction.id}>
              <Link
                to="/auctions/$auctionId"
                params={{ auctionId: String(auction.id) }}
                className="block group"
              >
                <StorefrontCard className="overflow-hidden p-3.5 group-hover:border-brand-300 transition-all">
                  <div className="flex gap-3.5">
                    {auction.photo_url ? (
                      <img
                        src={auction.photo_url}
                        alt={productName}
                        className="h-24 w-24 shrink-0 rounded-xl object-cover"
                      />
                    ) : (
                      <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-3xl">
                        🔨
                      </div>
                    )}
                    <div className="min-w-0 flex-1 flex flex-col justify-between">
                      <div>
                        <p className="line-clamp-2 text-sm font-semibold text-slate-900 group-hover:text-brand-600 transition-colors">
                          {productName}
                        </p>
                        <p className="mt-1.5 text-base font-bold text-brand-600">
                          {auction.current_price} UAH
                        </p>
                      </div>
                      <div className="mt-2 flex items-center gap-3 text-xs text-slate-500 pt-1 border-t border-slate-100">
                        <span className="font-medium">
                          {auction.bids_count} {T.bids}
                        </span>
                        <span className="inline-flex items-center gap-1 font-semibold text-amber-700 bg-amber-50 border border-amber-200/60 px-2 py-0.5 rounded-md">
                          <Timer className="h-3.5 w-3.5" />
                          {formatTimeLeft(auction.time_left_seconds, lang)}
                        </span>
                      </div>
                    </div>
                  </div>
                </StorefrontCard>
              </Link>
            </li>
          )
        })}
      </ul>
    </StorefrontPage>
  )
}

